import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ToastContainer, toast } from 'react-toastify';
import { Student } from '../../../../models/_models';
import 'react-toastify/dist/ReactToastify.css';

const OTHER = '__other__';

// The Type column on the admin composites table. For an administrator the Type
// is the title printed on the composite ("Assistant Principal: Mrs. Garcia"),
// so it offers the standard titles instead of free text. The column was free
// text for years and schools use titles outside the list ("Dean of
// Instruction"), so a value not in the list is kept and shown, and "Other..."
// still takes free text.
const UserTypeSelect = ({ studentId, value, options }) => {
  const [current, setCurrent] = useState(value || '');
  const [typingOther, setTypingOther] = useState(false);
  const otherInput = useRef(null);
  // Escape unmounts the text box, and newer browsers fire blur on a focused
  // element as it is removed -- which would save what Escape meant to discard.
  const cancelled = useRef(false);

  useEffect(() => { setCurrent(value || ''); }, [value]);
  useEffect(() => {
    if (!typingOther) return;
    cancelled.current = false;
    otherInput.current?.focus();
  }, [typingOther]);

  const save = (newValue) => {
    const trimmed = (newValue || '').trim();
    setTypingOther(false);
    if (trimmed === current) return;

    Student.find(studentId)
      .then(({ data }) => {
        data.userType = trimmed;
        return data.save();
      })
      .then((saved) => {
        if (!saved) throw new Error('not saved');
        setCurrent(trimmed);
        toast('Student was updated successfully', { containerId: studentId });
      })
      .catch(() => toast.error('There was an error saving the student', { containerId: studentId }));
  };

  const choices = current && !options.includes(current) ? [current, ...options] : options;

  return (
    <>
      <ToastContainer enableMultiContainer autoClose={1000} position="bottom-right" containerId={studentId} />
      {typingOther ? (
        <input
          ref={otherInput}
          type="text"
          defaultValue=""
          placeholder="Title"
          style={{ width: '100%', minHeight: '40px' }}
          onBlur={(e) => { if (!cancelled.current) save(e.target.value); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.target.blur();
            if (e.key === 'Escape') {
              cancelled.current = true;
              setTypingOther(false);
            }
          }}
        />
      ) : (
        <select
          value={current}
          style={{ width: '100%', minHeight: '40px' }}
          onChange={(e) => (e.target.value === OTHER ? setTypingOther(true) : save(e.target.value))}
        >
          <option value="">&nbsp;</option>
          {choices.map((choice) => <option key={choice} value={choice}>{choice}</option>)}
          <option value={OTHER}>Other…</option>
        </select>
      )}
    </>
  );
};

UserTypeSelect.propTypes = {
  studentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  value: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
};

UserTypeSelect.defaultProps = { value: '' };

export default UserTypeSelect;
